import { differenceInCalendarDays } from "date-fns";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { LegacyFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { Condition } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import { DATE_CONDITION_MODIFIERS } from "@app/util/viewModel/const";

// A check-in cutoff date reads as days since the last check-in, so the operator flips: on or
// before the date means no check-in for at least that many days.
const dateOperatorToDaysOperator: Partial<Record<PlatformFilterOperator, FilterOperator>> = {
  [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: FilterOperator.LESS_THAN_OR_EQUAL,
  [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: FilterOperator.GREATER_THAN_OR_EQUAL,
};

// The Field Agent filters "no check-in in the last N days" with a plain cutoff date, while the
// "No check-in for" filter models a number of days. Parses only that single-cutoff shape, one
// $lte or $gte date; interval conditions are parsed before this runs, and any other shape,
// such as a two-bound date range, returns undefined.
const parseCheckInCutoffDateCondition = (
  platformCondition: Condition
): LegacyFilterCondition | undefined => {
  const [platformOperator, ...rest] = Object.keys(platformCondition).filter(
    (key) => !DATE_CONDITION_MODIFIERS.includes(key)
  ) as PlatformFilterOperator[];
  const operator = rest.length ? undefined : dateOperatorToDaysOperator[platformOperator];
  const value = platformCondition[platformOperator];
  if (!operator || typeof value !== "string") {
    return undefined;
  }

  const date = parseApiDate(value);
  if (!isValidDate(date)) {
    return undefined;
  }

  return { operator, value: Math.max(0, differenceInCalendarDays(new Date(), date)) };
};

export default parseCheckInCutoffDateCondition;
